import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Plane, Eye, EyeOff, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { FirstPasswordChange } from '../components/FirstPasswordChange';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Unico camino de alta: el superadmin crea la aerolinea e invita a su administrador por correo.
export default function LoginPage() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [tempToken, setTempToken] = useState(null);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const userData = await login(loginEmail, loginPassword);
            if (userData?.requires_password_change) {
                setTempToken(userData.tempToken);
                return;
            }
            toast.success('Bienvenido a AeroOps');
            if (userData?.role === 'gh_supervisor' || userData?.role === 'gh_leader') {
                navigate('/gh');
                return;
            }
            if (['pilot', 'cabin_crew'].includes(userData?.role)) {
                navigate('/mis-vuelos');
                return;
            }
            if (userData?.role === 'admin') {
                const ob = await axios.get(`${API}/onboarding`).catch(() => null);
                if (ob?.data && !ob.data.completed) {
                    navigate('/onboarding');
                    return;
                }
            }
            navigate('/dashboard');
        } catch (error) {
            const d = error.response?.data?.detail;
            toast.error(typeof d === 'string' ? d : d?.message || 'No se pudo iniciar sesión. Revisa tu correo y contraseña.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen login-bg flex items-center justify-center p-4">
            <div className="login-overlay absolute inset-0" />

            <a href="/" className="absolute top-6 left-6 z-20 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-landing">
                <ArrowLeft className="w-4 h-4" />
                Volver al inicio
            </a>

            <Card className="w-full max-w-md relative z-10 bg-card/95 backdrop-blur-xl border-border/50 animate-fade-in">
                <CardHeader className="text-center pb-2">
                    <div className="mx-auto w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                        <Plane className="w-8 h-8 text-primary" />
                    </div>
                    <CardTitle className="text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed']">AeroOps</CardTitle>
                    <CardDescription className="text-muted-foreground">Sistema de Control de Operaciones Aéreas</CardDescription>
                </CardHeader>

                <CardContent>
                    {tempToken ? (
                        <FirstPasswordChange tempToken={tempToken} currentPassword={loginPassword}
                            onDone={(u) => navigate(['pilot', 'cabin_crew'].includes(u?.role) ? '/mis-vuelos' : '/dashboard')} />
                    ) : (
                    <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                        <div className="space-y-2">
                            <Label htmlFor="login-email">Correo electrónico</Label>
                            <Input id="login-email" type="email" placeholder="usuario@aerolinea.com" value={loginEmail}
                                onChange={(e) => setLoginEmail(e.target.value)} required data-testid="login-email" className="input-aviation" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="login-password">Contraseña</Label>
                            <div className="relative">
                                <Input id="login-password" type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={loginPassword}
                                    onChange={(e) => setLoginPassword(e.target.value)} required data-testid="login-password" className="input-aviation pr-10" />
                                <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label="Mostrar contraseña"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="login-toggle-password">
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        <div className="text-right">
                            <a href="/forgot-password" className="text-sm text-primary hover:underline" data-testid="forgot-password-link">Olvidé mi contraseña</a>
                        </div>
                        <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="login-submit">
                            {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                            Iniciar sesión
                        </Button>
                    </form>
                    )}

                    <div className="mt-6 pt-4 border-t border-border/50 text-center space-y-2">
                        <p className="text-xs text-muted-foreground" data-testid="login-no-account-note">
                            ¿Tu aerolínea aún no usa AeroOps? Las cuentas las crea nuestro equipo.{' '}
                            <a href="/#contact" className="text-primary hover:underline" data-testid="login-request-demo-link">Solicita una demo</a>
                        </p>
                        <p className="text-[11px] text-muted-foreground" data-testid="login-legal-links">
                            Al iniciar sesión aceptas los <a href="/terminos" className="underline hover:text-foreground">Términos de servicio</a> y la{' '}
                            <a href="/privacidad" className="underline hover:text-foreground">Política de privacidad</a>.
                        </p>
                        <a href="/admin/login" className="text-xs text-muted-foreground hover:text-primary transition-colors block" data-testid="admin-access-link">Acceso Administración</a>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
